import { createContext, Dispatch, SetStateAction, useContext } from 'react';
import { ChampionshipGame, Maybe, MmlEvent, MmlEventType, Stream, WscContent } from 'types/generated-types';

export enum PendingSwap {
  PENDING,
  NONE,
}

export const SharedVideoContext = createContext<SharedVideoContextType>({
  bracketId: '',
  broadcaster: undefined,
  disablePreroll: false,
  event: undefined,
  eventType: undefined,
  hasExcitementAlert: false,
  openContinuousPlayModal: false,
  game: undefined,
  isMultigame: undefined,
  isPrimary: undefined,
  mediaId: '',
  stream: undefined,
  title: '',
  useInlineFullscreen: undefined,
  transportContainerReady: undefined,
  pendingSwap: PendingSwap.NONE,
  swapContainerBracketId: undefined,
  canPlayVideo: false,
  isMarqueeActive: false,
  activeTransportContainer: undefined,
  setBracketId: () => undefined,
  setBroadcaster: () => undefined,
  setDisablePreroll: () => undefined,
  setEvent: () => undefined,
  setEventType: () => undefined,
  setHasExcitementAlert: () => undefined,
  setOpenContinuousPlayModal: () => undefined,
  setGame: () => undefined,
  setIsMultigame: () => undefined,
  setIsPrimary: () => undefined,
  setMediaId: () => undefined,
  setStream: () => undefined,
  setTitle: () => undefined,
  setUseInlineFullscreen: () => undefined,
  setTransportContainerReady: () => undefined,
  setPendingSwap: () => undefined,
  setSwapContainerBracketId: () => undefined,
  setCanPlayVideo: () => false,
  resetCanPlayVideo: () => {
    return;
  },
  setActiveTransportContainer: () => undefined,
  setIsMarqueeActive: () => undefined,
  resetContext: () => {
    return;
  },
  resetVideoParams: () => {
    return;
  },
});
SharedVideoContext.displayName = 'SharedVideoContext';

export type SharedVideoContextType = {
  bracketId: string;
  swapContainerBracketId?: string;
  broadcaster?: string;
  disablePreroll: boolean;
  event?: MmlEvent;
  eventType?: Maybe<MmlEventType>;
  hasExcitementAlert: boolean;
  openContinuousPlayModal?: boolean;
  game?: ChampionshipGame;
  isMultigame?: boolean;
  isPrimary?: boolean;
  mediaId: string;
  stream?: Stream | WscContent;
  title: string;
  useInlineFullscreen?: boolean;
  transportContainerReady: string | undefined | null;
  pendingSwap: PendingSwap;
  canPlayVideo: boolean;
  isMarqueeActive: boolean;
  activeTransportContainer: string | undefined | null;
  setBracketId: Dispatch<SetStateAction<string | undefined>>;
  setBroadcaster: Dispatch<SetStateAction<string | undefined>>;
  setDisablePreroll: Dispatch<SetStateAction<boolean | undefined>>;
  setEvent: Dispatch<SetStateAction<MmlEvent | undefined>>;
  setEventType: Dispatch<SetStateAction<MmlEventType | null | undefined>>;
  setHasExcitementAlert: Dispatch<SetStateAction<boolean | undefined>>;
  setOpenContinuousPlayModal: Dispatch<SetStateAction<boolean | undefined>>;
  setGame: Dispatch<SetStateAction<ChampionshipGame | undefined>>;
  setIsMultigame: Dispatch<SetStateAction<boolean | undefined>>;
  setIsPrimary: Dispatch<SetStateAction<boolean | undefined>>;
  setMediaId: Dispatch<SetStateAction<string | null | undefined>>;
  setStream: Dispatch<SetStateAction<Stream | WscContent | undefined>>;
  setTitle: Dispatch<SetStateAction<string | undefined>>;
  setUseInlineFullscreen: Dispatch<SetStateAction<boolean | undefined>>;
  setPendingSwap: Dispatch<SetStateAction<PendingSwap>>;
  setTransportContainerReady: Dispatch<SetStateAction<string | undefined | null>>;
  setSwapContainerBracketId: Dispatch<SetStateAction<string | undefined>>;
  setCanPlayVideo: Dispatch<SetStateAction<boolean>>;
  setIsMarqueeActive: Dispatch<SetStateAction<boolean>>;
  resetCanPlayVideo: () => void;
  setActiveTransportContainer: Dispatch<SetStateAction<string | undefined | null>>;
  resetContext: () => void;
  resetVideoParams: () => void;
};

export const useSharedVideoContext = (): SharedVideoContextType => useContext(SharedVideoContext);
